// ── BCHNostr Paid Verification (NIP-05) ──────────────────────────
//
// BCHNostr's verified badge is *earned by paying a BCH fee*, not by
// typing a NIP-05 into a profile. The workflow is:
//
//   1. User pays the BCH verification fee (see Nip05ClaimSection in
//      SettingsPage) and publishes a kind 30078 claim event.
//   2. An administrator / backend adds the user to the authoritative
//      `https://bchnostr.com/.well-known/nostr.json` directory.
//   3. This module is the *only* thing that grants the badge: it fetches
//      that directory and confirms name → pubkey matches the user.
//
// So the directory file is the single source of truth. There is no
// hardcoded allowlist and no local database — a user is verified if and
// only if the live BCHNostr NIP-05 file says so.
//
// ── Why the whole directory, not one lookup per user ──
//
// Every BCHNostr badge resolves against ONE domain, so `?name=` queries
// would mean one HTTP request per user per feed. Fetching the directory
// once answers every lookup on the page. `?name=` remains as a fallback
// for hosts that only serve single-name responses.
//
// ── Trust boundary ──
//
// A `nip05` field in a kind 0 profile is a *claim*, not proof — anyone
// can put `satoshi@bchnostr.com` in their profile. It is only ever
// believed after the directory confirms the pubkey, which is what stops
// users minting themselves a paid badge for free.

import { verifyEvent, type Event } from 'nostr-tools';
import { GRANT_ISSUERS } from './platform';

const NIP05_DOMAIN = 'bchnostr.com';
const DIRECTORY_URL = `https://${NIP05_DOMAIN}/.well-known/nostr.json`;

// ── Live operator grants ─────────────────────────────────────────
//
// The static directory file is the baseline. On top of it we merge
// verification GRANT events pulled live from the relays, so a badge
// issued by the automated backend (see netlify/functions/verify-claims)
// appears WITHOUT a redeploy. A grant is a kind 30078 event from one of
// GRANT_ISSUERS with d-tag `bchnostr/nip05-override/<name>` and a `p` tag
// naming the granted pubkey. Signatures are verified locally, so trusting
// these is safe — only the operator-controlled keys can issue them.
const GRANT_KIND = 30078;
const GRANT_D_PREFIX = 'bchnostr/nip05-override/';
const GRANT_RELAYS = [
  'wss://relay.bchnostr.com',
  'wss://nos.lol',
  'wss://relay.primal.net',
  'wss://relay.nostr.net',
];
const GRANT_QUERY_TIMEOUT = 6000;

/** How long a loaded directory is considered fresh. */
const DIRECTORY_TTL = 10 * 60 * 1000; // 10 minutes

/** Periodic background refresh, so newly-paid users light up on their own. */
const REFRESH_INTERVAL = 10 * 60 * 1000; // 10 minutes

/**
 * How long a previously-confirmed verification survives while the
 * directory is unreachable.
 *
 * This is the "fail gracefully" rule: an outage or a flaky connection
 * must not strip badges from users who genuinely paid. We keep trusting
 * the last good answer for a day while retrying in the background.
 */
const STALE_GRACE = 24 * 60 * 60 * 1000; // 24 hours

/** Backoff after a failed fetch, so a dead host isn't hammered. */
const ERROR_RETRY_DELAY = 60 * 1000; // 1 minute

const STORAGE_KEY = 'bchnostr:nip05-directory';

// ══════════════════════════════════════════════════════════════════
//  STATE
// ══════════════════════════════════════════════════════════════════

/** name (lowercased) → pubkey (hex), straight from the directory. */
type NameMap = Record<string, string>;

interface DirectoryState {
  names: NameMap;
  /** When this data was successfully fetched. 0 = never. */
  fetchedAt: number;
  /** Last fetch attempt, successful or not. */
  attemptedAt: number;
  /** True when the most recent attempt failed. */
  degraded: boolean;
}

const state: DirectoryState = {
  names: {},
  fetchedAt: 0,
  attemptedAt: 0,
  degraded: false,
};

// Subscribers are notified whenever the directory changes, so badges
// appear without needing a page reload.
const listeners = new Set<() => void>();

function notify(): void {
  for (const fn of listeners) {
    try { fn(); } catch { /* a broken listener must not break the rest */ }
  }
}

/** Subscribe to directory updates. Returns an unsubscribe function. */
export function onVerificationChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

// ── Persistence ───────────────────────────────────────────────────
//
// localStorage (not sessionStorage) so a returning visitor sees correct
// badges on first paint instead of a flash of unverified names.

function loadPersisted(): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as { names?: unknown; fetchedAt?: unknown };
    const names = sanitizeNames(parsed.names);
    if (!names) return;
    state.names = names;
    state.fetchedAt = typeof parsed.fetchedAt === 'number' ? parsed.fetchedAt : 0;
  } catch {
    // Corrupt or unavailable storage — we simply start cold.
  }
}

function persist(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      names: state.names,
      fetchedAt: state.fetchedAt,
    }));
  } catch {
    // Quota or private mode — verification still works, just not cached.
  }
}

loadPersisted();

// ══════════════════════════════════════════════════════════════════
//  VALIDATION
// ══════════════════════════════════════════════════════════════════

const HEX64 = /^[0-9a-f]{64}$/;

/**
 * Coerce an untrusted parsed body into a clean name → pubkey map.
 *
 * Returns null when the payload isn't a usable NIP-05 document. This
 * guard matters more than it looks: a misconfigured host (or an SPA
 * catch-all route) happily serves `index.html` for this path, and
 * without validation that would be treated as "directory loaded, no
 * users present" — silently unverifying everybody.
 */
function sanitizeNames(input: unknown): NameMap | null {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return null;

  const out: NameMap = {};
  for (const [rawName, rawPubkey] of Object.entries(input as Record<string, unknown>)) {
    if (typeof rawPubkey !== 'string') continue;
    const pubkey = rawPubkey.trim().toLowerCase();
    // Only 32-byte hex keys are valid NIP-05 targets.
    if (!HEX64.test(pubkey)) continue;
    const name = rawName.trim().toLowerCase();
    if (!name) continue;
    out[name] = pubkey;
  }
  return out;
}

/** Extract and validate the `names` object from a NIP-05 response body. */
function parseDirectory(body: unknown): NameMap | null {
  if (!body || typeof body !== 'object') return null;
  const names = (body as { names?: unknown }).names;
  // A response without a `names` object is not a NIP-05 document.
  if (names === undefined) return null;
  return sanitizeNames(names);
}

// ══════════════════════════════════════════════════════════════════
//  NAME HANDLING
// ══════════════════════════════════════════════════════════════════

/**
 * Is this profile field even claiming a BCHNostr identifier?
 *
 * A fast string test only — it proves nothing about whether the claim is
 * real. Tolerates the common `@bchnostr` typo (missing `.com`).
 */
export function claimsBchnostrNip05(nip05: string | undefined | null): boolean {
  if (!nip05 || typeof nip05 !== 'string') return false;
  const trimmed = nip05.trim().toLowerCase();
  return trimmed.endsWith('@' + NIP05_DOMAIN) || trimmed.endsWith('@bchnostr');
}

/** The local part of a BCHNostr NIP-05, or null if it isn't one. */
function extractName(nip05: string | undefined | null): string | null {
  if (!claimsBchnostrNip05(nip05)) return null;
  const name = nip05!.trim().toLowerCase().replace(/@bchnostr(\.com)?$/, '');
  // NIP-05 allows `_` as a domain-level identifier.
  if (!name || !/^[a-z0-9_.\-]+$/.test(name)) return null;
  return name;
}

// ══════════════════════════════════════════════════════════════════
//  FETCHING
// ══════════════════════════════════════════════════════════════════

let inFlight: Promise<void> | null = null;

async function fetchJson(url: string): Promise<unknown | null> {
  const resp = await fetch(url, {
    signal: AbortSignal.timeout(8000),
    headers: { Accept: 'application/json' },
    // NIP-05 files are public; avoid sending cookies cross-origin.
    credentials: 'omit',
  });
  if (!resp.ok) return null;

  // Never trust Content-Type alone. Parse defensively and let
  // parseDirectory() decide whether the shape is usable.
  const text = await resp.text();
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

// ══════════════════════════════════════════════════════════════════
//  LIVE OPERATOR GRANTS (relay-sourced, signature-verified)
// ══════════════════════════════════════════════════════════════════

/** Query one relay for grant events. Resolves with an array, never rejects. */
function queryGrantRelay(url: string): Promise<Event[]> {
  return new Promise(resolve => {
    let ws: WebSocket | null = null;
    const events: Event[] = [];
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try { ws?.close(); } catch { /* already closed */ }
      resolve(events);
    };
    const timer = setTimeout(finish, GRANT_QUERY_TIMEOUT);
    try { ws = new WebSocket(url); } catch { finish(); return; }
    const subId = 'grant_' + Math.random().toString(36).slice(2, 10);
    ws.onopen = () => {
      try {
        ws!.send(JSON.stringify(['REQ', subId, {
          kinds: [GRANT_KIND],
          authors: GRANT_ISSUERS,
          limit: 5000,
        }]));
      } catch { finish(); }
    };
    ws.onmessage = (msg: MessageEvent) => {
      try {
        const data = JSON.parse(typeof msg.data === 'string' ? msg.data : String(msg.data));
        if (data[0] === 'EVENT' && data[1] === subId && data[2]) events.push(data[2] as Event);
        else if (data[0] === 'EOSE' && data[1] === subId) finish();
      } catch { /* malformed frame */ }
    };
    ws.onerror = finish;
    ws.onclose = finish;
  });
}

/**
 * Fetch live grants from the relays, verify each signature and issuer,
 * and merge the freshest grant per name into the directory.
 *
 * Grants are ADDITIVE — they never remove baseline entries. A grant only
 * takes effect after `verifyEvent` confirms it was really signed by a
 * trusted issuer, so a forged event cannot mint a badge.
 */
async function refreshGrants(): Promise<void> {
  if (typeof WebSocket === 'undefined') return;

  const perRelay = await Promise.all(GRANT_RELAYS.map(queryGrantRelay));
  const byId = new Map<string, Event>();
  for (const list of perRelay) for (const ev of list) if (ev?.id) byId.set(ev.id, ev);

  // name -> { pubkey, created_at } keeping the newest valid grant.
  const newest = new Map<string, { pubkey: string; created_at: number }>();

  for (const ev of byId.values()) {
    if (ev.kind !== GRANT_KIND) continue;
    if (!GRANT_ISSUERS.includes((ev.pubkey || '').toLowerCase())) continue;

    const d = ev.tags?.find(t => t[0] === 'd')?.[1] || '';
    if (!d.startsWith(GRANT_D_PREFIX)) continue;
    const name = d.slice(GRANT_D_PREFIX.length).trim().toLowerCase();
    if (!name) continue;

    let target = ev.tags?.find(t => t[0] === 'p')?.[1];
    if (!target) { try { target = JSON.parse(ev.content)?.pubkey; } catch { /* none */ } }
    if (typeof target !== 'string') continue;
    const pubkey = target.trim().toLowerCase();
    if (!HEX64.test(pubkey)) continue;

    // Verify the signature LAST — it's the expensive step, so we only pay
    // it for structurally-valid grants.
    let ok = false;
    try { ok = verifyEvent(ev); } catch { ok = false; }
    if (!ok) continue;

    const prev = newest.get(name);
    if (!prev || (ev.created_at || 0) > prev.created_at) {
      newest.set(name, { pubkey, created_at: ev.created_at || 0 });
    }
  }

  if (newest.size === 0) return;

  let changed = false;
  const merged: NameMap = { ...state.names };
  for (const [name, { pubkey }] of newest) {
    if (merged[name] !== pubkey) { merged[name] = pubkey; changed = true; }
  }
  if (changed) {
    state.names = merged;
    if (!state.fetchedAt) state.fetchedAt = Date.now();
    persist();
    notify();
  }
}

/**
 * Refresh the directory from the network.
 *
 * On failure the previous data is deliberately left in place and the
 * state is flagged `degraded`, so verified users keep their badges for
 * the grace window instead of blinking out during an outage.
 */
export function refreshDirectory(force = false): Promise<void> {
  const now = Date.now();

  if (!force) {
    // Fresh enough already.
    if (state.fetchedAt && now - state.fetchedAt < DIRECTORY_TTL) return Promise.resolve();
    // Back off after a recent failure.
    if (state.degraded && now - state.attemptedAt < ERROR_RETRY_DELAY) return Promise.resolve();
  }

  // Coalesce concurrent callers onto one request.
  if (inFlight) return inFlight;

  inFlight = (async () => {
    state.attemptedAt = Date.now();
    try {
      const body = await fetchJson(DIRECTORY_URL);
      const names = parseDirectory(body);

      if (!names) {
        // Reachable but not serving a valid NIP-05 document. Treated
        // exactly like an outage: keep the last good data.
        state.degraded = true;
        console.warn(
          `[nip05] ${DIRECTORY_URL} did not return a valid NIP-05 document ` +
          '(no "names" object). Verified badges are running on cached data. ' +
          'The domain must serve /.well-known/nostr.json as JSON.',
        );
        notify();
        return;
      }

      const changed = JSON.stringify(names) !== JSON.stringify(state.names);
      state.names = names;
      state.fetchedAt = Date.now();
      state.degraded = false;
      persist();
      if (changed) notify();
    } catch {
      // Network error / timeout / CORS. Preserve what we had.
      state.degraded = true;
      notify();
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

/**
 * Single-name lookup, used only when the full directory is unavailable.
 *
 * Some hosts serve `?name=` responses but not the whole file, so this
 * keeps those working rather than failing every badge.
 */
async function verifySingleName(name: string, pubkey: string): Promise<boolean | null> {
  try {
    const body = await fetchJson(`${DIRECTORY_URL}?name=${encodeURIComponent(name)}`);
    const names = parseDirectory(body);
    if (!names) return null; // unusable response — caller decides
    // Merge into the directory so repeat lookups are free.
    if (names[name]) {
      state.names = { ...state.names, ...names };
      persist();
    }
    return names[name] === pubkey.toLowerCase();
  } catch {
    return null;
  }
}

// ══════════════════════════════════════════════════════════════════
//  VERIFICATION
// ══════════════════════════════════════════════════════════════════

export type VerificationStatus =
  /** Directory confirms this name maps to this pubkey. */
  | 'verified'
  /** Directory loaded, but the name is absent or maps elsewhere. */
  | 'unverified'
  /** Directory has never loaded — genuinely unknown. */
  | 'unknown';

/**
 * Resolve verification from cached data only. Never performs I/O, so it
 * is safe to call during render.
 */
export function getVerificationStatus(
  nip05: string | undefined | null,
  pubkey: string | undefined | null,
): VerificationStatus {
  if (!pubkey) return 'unverified';
  const name = extractName(nip05);
  if (!name) return 'unverified';

  // Nothing cached yet — don't claim either way.
  if (!state.fetchedAt) return 'unknown';

  const mapped = state.names[name];
  if (mapped && mapped === pubkey.toLowerCase()) {
    // Positive result. While the directory is reachable this is simply
    // current; while degraded we honour it for the grace window so an
    // outage can't strip a paid badge.
    if (!state.degraded) return 'verified';
    return Date.now() - state.fetchedAt < STALE_GRACE ? 'verified' : 'unknown';
  }

  // The name is missing or points at a different pubkey. If the data is
  // stale *and* we currently can't reach the host, admit we don't know
  // rather than asserting a negative from old data — a user who paid
  // moments ago may simply not be in our copy yet.
  if (state.degraded && Date.now() - state.fetchedAt > DIRECTORY_TTL) return 'unknown';

  return 'unverified';
}

/** Convenience boolean. Only ever true for a confirmed match. */
export function isVerified(
  nip05: string | undefined | null,
  pubkey: string | undefined | null,
): boolean {
  return getVerificationStatus(nip05, pubkey) === 'verified';
}

/**
 * The verified @bchnostr.com handle for a pubkey, resolved from the
 * directory ALONE — independent of what the profile's `nip05` field says.
 *
 * This is the reverse of `getVerificationStatus`: instead of asking "does
 * this claimed name map to this pubkey", it asks "which name (if any) does
 * the authoritative directory / operator grant assign to this pubkey".
 *
 * Why this exists: a kind 0 profile can be re-published with an empty
 * `nip05` field (some clients blank fields they don't manage), which would
 * otherwise strip a badge the user genuinely paid for. The directory is the
 * source of truth for the badge, so we honour it even when the profile no
 * longer echoes the claim.
 *
 * Returns the full `name@bchnostr.com` identifier, or null when the pubkey
 * is not in the directory (or nothing has loaded yet).
 */
export function getVerifiedNip05ForPubkey(
  pubkey: string | undefined | null,
): string | null {
  if (!pubkey) return null;
  if (!state.fetchedAt) return null;

  // Honour the grace window during an outage, exactly like the forward
  // lookup, so a directory blip can't strip the handle.
  if (state.degraded && Date.now() - state.fetchedAt >= STALE_GRACE) return null;

  const target = pubkey.toLowerCase();
  for (const [name, mapped] of Object.entries(state.names)) {
    if (mapped === target) return `${name}@${NIP05_DOMAIN}`;
  }
  return null;
}

/**
 * Is this pubkey verified, judged from the directory alone?
 *
 * Unlike `getVerificationStatus`, this does NOT require the profile to
 * still carry a matching `nip05` claim — the operator's signed directory
 * entry / grant is itself the proof. Used to keep badges attached to a
 * paid identity even after a profile edit blanks the nip05 field.
 */
export function isPubkeyVerifiedByDirectory(
  pubkey: string | undefined | null,
): boolean {
  return getVerifiedNip05ForPubkey(pubkey) !== null;
}

/**
 * Ensure the directory is loaded, then report verification.
 *
 * Falls back to a single-name lookup when the directory itself can't be
 * used, so verification still works on hosts that only answer `?name=`.
 */
export async function verifyBchnostrNip05(
  nip05: string | undefined | null,
  pubkey: string | undefined | null,
): Promise<boolean> {
  const name = extractName(nip05);
  if (!name || !pubkey) return false;

  await refreshDirectory();

  const status = getVerificationStatus(nip05, pubkey);
  if (status === 'verified') return true;

  // Directory unusable — try the per-name endpoint before giving up.
  if (status === 'unknown' || (state.degraded && !state.names[name])) {
    const single = await verifySingleName(name, pubkey);
    if (single !== null) {
      if (single) notify();
      return single;
    }
    return false;
  }

  return false;
}

/**
 * Kick off verification for a batch of pubkeys.
 *
 * One directory fetch covers the whole batch, so this is O(1) requests
 * regardless of feed size.
 */
export async function triggerVerificationForPubkeys(
  pubkeys: string[],
  getProfile: (pk: string) => { nip05?: unknown } | null,
): Promise<void> {
  const anyClaims = pubkeys.some(pk => {
    const profile = getProfile(pk);
    return claimsBchnostrNip05(typeof profile?.nip05 === 'string' ? profile.nip05 : '');
  });
  // Don't fetch the directory for a feed with no BCHNostr claims at all.
  if (!anyClaims) return;
  await refreshDirectory();
}

/**
 * Synchronous check by pubkey alone, for feed filtering.
 *
 * Needs the profile to read the claimed name, since verification is a
 * statement about a name → pubkey pair, not about a pubkey by itself.
 */
export function isVerifiedPubkeyCached(
  pubkey: string,
  getProfile?: (pk: string) => { nip05?: unknown } | null,
): boolean {
  if (!pubkey || !getProfile) return false;
  const profile = getProfile(pubkey);
  const nip05 = typeof profile?.nip05 === 'string' ? profile.nip05 : '';
  return isVerified(nip05, pubkey);
}

/**
 * Drop cached state for a name and re-fetch.
 *
 * Called right after someone completes the paid claim (and after an
 * operator grant) so the badge appears as soon as the administrator has
 * added them, without waiting for the next scheduled refresh.
 *
 * Passing `pubkey` additionally performs a targeted `?name=` lookup, which
 * catches the case where the directory response is cached by a CDN but the
 * single-name endpoint is already up to date.
 *
 * NOTE: this only ever *re-reads* the authoritative directory. It never
 * writes an unconfirmed name → pubkey pair into the cache, so completing
 * payment cannot by itself grant a badge — the directory still decides.
 */
export function invalidateNip05Cache(nip05?: string, pubkey?: string): void {
  const name = extractName(nip05);
  if (name) {
    const next = { ...state.names };
    delete next[name];
    state.names = next;
  }
  state.fetchedAt = 0;
  state.degraded = false;
  persist();

  refreshDirectory(true).then(async () => {
    // If the full directory still doesn't list this name, try the
    // per-name endpoint before giving up — CDNs often serve a stale
    // copy of the whole file while `?name=` is already current.
    if (name && pubkey && state.names[name] !== pubkey.toLowerCase()) {
      await verifySingleName(name, pubkey).catch(() => null);
    }
    notify();
  });

  // Also pull live grants — a just-paid claim is auto-granted by the
  // backend within a couple of minutes, and this makes the badge appear
  // as soon as that grant lands rather than at the next scheduled refresh.
  refreshGrants().catch(() => {});
}

/** Number of verified identities currently known. */
export function getVerifiedPubkeyCount(): number {
  return Object.keys(state.names).length;
}

/**
 * Is a username still free? Used by the paid-claim form before charging.
 *
 * - `available` — nobody holds this name
 * - `mine`      — already mapped to this pubkey (re-claim / already paid)
 * - `taken`     — held by a different pubkey
 * - `unknown`   — directory unreachable, so we genuinely can't say
 *
 * Callers should treat `unknown` as non-blocking (let the user proceed)
 * rather than refusing the claim during an outage.
 */
export async function checkNameAvailability(
  name: string,
  pubkey: string | undefined | null,
): Promise<'available' | 'mine' | 'taken' | 'unknown'> {
  const normalized = name.trim().toLowerCase();
  if (!normalized || !/^[a-z0-9_.\-]+$/.test(normalized)) return 'unknown';

  // The per-name endpoint is authoritative and avoids acting on a
  // CDN-cached copy of the full directory.
  const body = await fetchJson(
    `${DIRECTORY_URL}?name=${encodeURIComponent(normalized)}`,
  ).catch(() => null);

  const names = parseDirectory(body);
  if (!names) return 'unknown';

  const holder = names[normalized];
  if (!holder) return 'available';
  return pubkey && holder === pubkey.toLowerCase() ? 'mine' : 'taken';
}

/**
 * The current directory contents, for the operator recovery tool.
 *
 * Returned as a fresh copy so callers can't mutate internal state.
 */
export function getDirectoryNames(): NameMap {
  return { ...state.names };
}

/**
 * Build a complete `/.well-known/nostr.json` body.
 *
 * Merges the live directory with additional name → pubkey pairs, so
 * regenerating the file after recovering paid claims never drops the
 * users who are already verified.
 */
export function buildDirectoryJson(additions: NameMap): string {
  const merged: NameMap = { ...state.names };
  for (const [rawName, rawPubkey] of Object.entries(additions)) {
    const name = rawName.trim().toLowerCase();
    const pubkey = rawPubkey.trim().toLowerCase();
    if (!name || !HEX64.test(pubkey)) continue;
    merged[name] = pubkey;
  }

  // Sort for a stable diff between regenerations.
  const names: NameMap = {};
  for (const key of Object.keys(merged).sort()) names[key] = merged[key];

  return JSON.stringify({ names }, null, 2) + '\n';
}

/** Diagnostics for the settings/admin UI. */
export function getDirectoryHealth(): {
  loaded: boolean;
  degraded: boolean;
  entryCount: number;
  fetchedAt: number;
} {
  return {
    loaded: state.fetchedAt > 0,
    degraded: state.degraded,
    entryCount: Object.keys(state.names).length,
    fetchedAt: state.fetchedAt,
  };
}

// ══════════════════════════════════════════════════════════════════
//  PERIODIC REFRESH
// ══════════════════════════════════════════════════════════════════

let refreshTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Start background refreshing. Safe to call repeatedly.
 *
 * Also refreshes when the tab regains focus: browsers throttle timers in
 * background tabs, so a long-lived tab would otherwise hold stale data.
 */
export function initNip05Verification(): void {
  refreshDirectory();
  refreshGrants().catch(() => {});

  if (refreshTimer !== null) return;
  refreshTimer = setInterval(() => {
    refreshDirectory();
    refreshGrants().catch(() => {});
  }, REFRESH_INTERVAL);

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        refreshDirectory();
        refreshGrants().catch(() => {});
      }
    });
  }
}
